import { useContext, useState } from "react";
import axios from "axios";
import AuthContext from "/context/AuthProvider";
import store from "/redux/state";
import { isOpenRegistationFormPresentInUrl } from "../isOpenRegistationForm";
import { t } from "./useCustomTranslation";

/**
 * @typedef {import('axios').AxiosInstance & { wrap: (x: Promise<any>) => Promise<any> }} AxiosInstanceWithWrap
 */
/**
 * todo: drop this hook, replace axios with fetch, remove auth context
 * @returns {import("axios").AxiosInstance & { wrap: (x: Promise<any>) => Promise<any> }}
 */
export const useAxiosWithAuth = () => {
  const { auth } = useContext(AuthContext);
  const wrap = (x) =>
    x
      .then((res) => res.data)
      .catch(async (err) => {
        Error.captureStackTrace &&
          Error.captureStackTrace(err, useAxiosWithAuth);
        if (err.response && err.response.data) {
          switch (err.response.data) {
            case "Server Error": {
              store.setPopUp("confirm", {
                title: t("message.somethingWentWrong"),
                subtitle: t("message.serverError"),
                close: t("labels.okay"),
              });
              break;
            }
          }
          return Promise.reject(err.response.data);
        }
        return Promise.reject(err);
      });
  const x = /** @type {AxiosInstanceWithWrap} */ (
    axios.create({
      baseURL: import.meta.env.VITE_REACT_APP_API_BASE_URL,
      withCredentials: true,
      headers: (() => {
        const base = {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(auth?.token
            ? { Authorization: `Bearer ${auth.token}` }
            : { "X-Trans-Language": "en" }),
        };
        const attrib = getAzugaAttributionHeader();
        return attrib["X-Azuga-Attribution"]
          ? { ...base, "X-Azuga-Attribution": attrib["X-Azuga-Attribution"] }
          : base;
      })(),
    })
  );
  // Keep attribution header fresh per request
  x.interceptors.request.use((config) => {
    try {
      const header = getAzugaAttributionHeader();
      const val = header["X-Azuga-Attribution"];
      if (!val) return config;
      const anyConfig = /** @type {any} */ (config);
      if (!anyConfig.headers) {
        /** @type {any} */ (anyConfig).headers = {};
      }
      if (typeof anyConfig.headers.set === "function") {
        anyConfig.headers.set("X-Azuga-Attribution", val);
      } else {
        anyConfig.headers["X-Azuga-Attribution"] = val;
      }
    } catch {}
    return config;
  });
  x.wrap = wrap;
  return x;
};

const useAxiosREST = () => {
  const [isFetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const { auth, setAuth } = useContext(AuthContext);

  const URL = import.meta.env.VITE_REACT_APP_API_BASE_URL;

  const bearer = auth?.token
    ? { Authorization: `Bearer ${auth.token}` }
    : { "X-Trans-Language": "en" };
  // Create axios instance
  const client = axios.create({
    baseURL: URL,
    withCredentials: true,
    headers: (() => {
      const base = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...bearer,
      };
      const attrib = getAzugaAttributionHeader();
      return attrib["X-Azuga-Attribution"]
        ? { ...base, "X-Azuga-Attribution": attrib["X-Azuga-Attribution"] }
        : base;
    })(),
  });

  // Keep attribution header fresh per request
  client.interceptors.request.use((config) => {
    try {
      const header = getAzugaAttributionHeader();
      const val = header["X-Azuga-Attribution"];
      if (!val) return config;
      const anyConfig = /** @type {any} */ (config);
      if (!anyConfig.headers) {
        /** @type {any} */ (anyConfig).headers = {};
      }
      if (typeof anyConfig.headers.set === "function") {
        anyConfig.headers.set("X-Azuga-Attribution", val);
      } else {
        anyConfig.headers["X-Azuga-Attribution"] = val;
      }
    } catch {}
    return config;
  });

  const checkError = (err, end = "") => {
    const msg = err.response?.data?.message;

    switch (msg) {
      case "Unauthenticated.":
        localStorage.removeItem("user_country");
        setAuth({ token: null });
        if (end !== "user/profile") {
          if (typeof auth?.token !== "string") {
            // Prompt user to log in only if token was set to real value
            break;
          }
          if (isOpenRegistationFormPresentInUrl()) {
            store.setPopUp("reg");
          } else {
            store.setPopUp("login");
          }
        }
        break;
      case "Server Error":
        store.setPopUp("confirm", {
          title: t("message.somethingWentWrong"),
          subtitle: t("message.serverError"),
          close: t("labels.okay"),
        });
        break;
      default: {
        if (!err.response) {
          console.error(err);
        }
      }
    }
    setFetching(false);
    setError(err);
  };

  const fetchAPI = {
    get: async (end, params = null) => {
      setFetching(true);
      try {
        const res = await client.get(end, { params });
        setFetching(false);
        return res.data;
      } catch (error) {
        checkError(error, end);

        switch (error.response.status) {
          case 404:
            return { not_found: true, ...error.response?.data };
          default:
            return error.response?.data;
        }
      }
    },
    post: async (end, data = null) => {
      setFetching(true);
      try {
        const res = await client.post(end, data);

        setFetching(false);
        return res.data;
      } catch (error) {
        checkError(error);
        return error.response?.data;
      }
    },
    delete: async (end, data = null) => {
      setFetching(true);
      try {
        const res = await client.delete(end, { data: data });
        setFetching(false);
        return res.data;
      } catch (error) {
        checkError(error);
        return error.response?.data;
      }
    },

    patch: async (end, data = null) => {
      setFetching(true);
      try {
        const res = await client.patch(end, data);
        setFetching(false);
        return res.data;
      } catch (error) {
        checkError(error);
        return error.response?.data;
      }
    },
    put: async (end, data = null) => {
      setFetching(true);
      try {
        const res = await client.put(end, data);
        setFetching(false);
        return res.data;
      } catch (error) {
        checkError(error);
        return error.response?.data;
      }
    },
  };

  return { isFetching, error, fetchAPI };
};

export default useAxiosREST;

/**
 * Reads azuga attribution cookie/localStorage and returns header map
 */
function getAzugaAttributionHeader() {
  try {
    const cookieName = "azuga_attrib";
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${cookieName}=`));
    let value = null;
    if (match) {
      try {
        value = JSON.parse(decodeURIComponent(match.split("=")[1]));
      } catch {}
    }
    if (!value) {
      const raw = localStorage.getItem(cookieName);
      if (raw) {
        try {
          value = JSON.parse(raw);
        } catch {}
      }
    }
    if (value && (value.affiliate_id || value.offer_id || value.etid)) {
      return {
        "X-Azuga-Attribution": JSON.stringify({
          affiliate_id: value.affiliate_id,
          offer_id: value.offer_id,
          etid: value.etid,
        }),
      };
    }
  } catch {}
  return {};
}
